/**
 * Admin → Users → a client: the Money card (2026-10-13, Leon: "do it on Platform as well"). The same layout as
 * the CRM's Money menu: grouped actions with short names, a tag saying how each works, a "?" with what it does,
 * and one dialog per action (big amount, balance after, optional note for the client). The endpoints are the
 * ones the old cash and credit forms used. Booked at once, unless the staff member's Dealer desk role may only
 * request it (past their direct limit): then the server files an approval (202, APR-…) and the dialog says so.
 */
import { useState, type FormEvent } from 'react';
import { D, fmtUsd, type AccountSummary } from '@platform/shared';
import { cx, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

type Key = 'deposit' | 'credit_add' | 'withdrawal' | 'credit_remove';
interface Action {
  key: Key;
  group: 'Money in' | 'Money out';
  label: string;
  /** The ledger and wallet line when no note is given. */
  fallback: string;
  out: boolean;
  credit: boolean;
  what: string;
  facts: [tone: 'good' | 'warn' | '', text: string][];
  warn?: string;
  steps: string[];
}

const ACTIONS: Action[] = [
  {
    key: 'deposit', group: 'Money in', label: 'Deposit', fallback: 'Booked by our team', out: false, credit: false,
    what: 'Adds cash to the client’s balance straight away. It counts as a real deposit: in deposit totals, and as their first deposit if it is one.',
    facts: [['good', 'Booked at once (or sent for approval, by your role)'], ['good', 'Changes the cash balance'], ['', 'Client sees it']],
    steps: ['Pick the account (live or demo) and enter the amount in USD.', 'Add a note for the client if you like: it shows on the line in their transactions.', 'Press Add. Past the source-of-funds threshold an unverified client needs the check done first.'],
  },
  {
    key: 'credit_add', group: 'Money in', label: 'Trading credit', fallback: 'Trading credit', out: false, credit: true,
    what: 'Adds trading credit to the live account: usable as margin and counted in equity, never withdrawable, never used up by losses.',
    facts: [['good', 'Booked at once (or sent for approval, by your role)'], ['good', 'Changes the credit, not the cash'], ['', 'Client sees it']],
    steps: ['Enter the amount in USD.', 'Add a note for the client if you like (for example the promotion).', 'Press Add.'],
  },
  {
    key: 'withdrawal', group: 'Money out', label: 'Withdrawal', fallback: 'Paid out by our team', out: true, credit: false,
    what: 'Takes cash off the client’s balance straight away and counts it as a withdrawal. Send the money to the client yourself.',
    facts: [['good', 'Booked at once (or sent for approval, by your role)'], ['good', 'Changes the cash balance'], ['', 'Client sees it']],
    warn: 'Only cash that is not backing open trades can be taken.',
    steps: ['Pick the account and enter the amount in USD.', 'Add a note for the client if you like.', 'Press Remove, then pay the client.'],
  },
  {
    key: 'credit_remove', group: 'Money out', label: 'Remove trading credit', fallback: 'Trading credit removed', out: true, credit: true,
    what: 'Takes trading credit back from the live account.',
    facts: [['good', 'Booked at once (or sent for approval, by your role)'], ['good', 'Changes the credit, not the cash'], ['', 'Client sees it']],
    warn: 'Only credit that is not backing open positions or orders can be removed.',
    steps: ['Enter the amount in USD.', 'Add a note for the client if you like.', 'Press Remove.'],
  },
];

/** The server's answer when the staff member's role may only request the action (Dealer desk → Approvals). */
type PendingApproval = { pendingApproval: { number: string } };
const isPending = (r: unknown): r is PendingApproval => !!r && typeof r === 'object' && 'pendingApproval' in r;

const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const FACT: Record<string, string> = { good: 'bg-up-bg text-up', warn: 'bg-warn-bg text-warn', '': 'border border-line text-muted' };

function Help({ a, onStart, onClose }: { a: Action; onStart: () => void; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={a.label} width={460}>
      <div className="flex flex-col gap-3 text-[13px]">
        <p className="m-0">{a.what}</p>
        <div className="flex flex-wrap gap-1.5">
          {a.facts.map(([tone, text]) => (
            <span key={text} className={cx('rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold', FACT[tone])}>
              {text}
            </span>
          ))}
        </div>
        {a.warn && <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">{a.warn}</p>}
        <div className="text-[11.5px] font-semibold tracking-wide text-muted uppercase">How to use it</div>
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {a.steps.map((s, i) => (
            <li key={s} className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5">
              <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-accent-bg text-[11.5px] font-bold text-accent">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Close
          </button>
          <button type="button" className={cx('h-8 rounded-md px-3 text-xs font-semibold text-white', a.out ? 'bg-btn-short' : 'bg-accent')} onClick={onStart}>
            {a.label}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ActionDialog({ a, accounts, email, onClose, onDone }: { a: Action; accounts: AccountSummary[]; email: string; onClose: () => void; onDone: () => void }) {
  const choices = a.credit ? accounts.filter((x) => x.kind === 'live') : accounts;
  const [accountId, setAccountId] = useState(choices.find((x) => x.kind === 'live')?.id ?? choices[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const acc = choices.find((x) => x.id === accountId);
  const ok = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0;
  const now = acc ? D.from(a.credit ? acc.credit : acc.balance) : null;
  const after = now && ok ? (a.out ? now.sub(amount) : now.add(amount)) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok || !acc) return;
    setBusy(true);
    setErr(null);
    const reason = note.trim() || a.fallback;
    try {
      const r = a.credit
        ? await api.post<unknown>(`/api/admin/accounts/${acc.id}/trading-credit`, { action: a.out ? 'remove' : 'add', amount, reason })
        : await api.post<unknown>(`/api/admin/accounts/${acc.id}/${a.out ? 'debit' : 'credit'}`, { amount, reason });
      onDone();
      if (isPending(r)) {
        setSent(r.pendingApproval.number);
        setBusy(false);
        return;
      }
      onClose();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Something went wrong');
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <Modal open onClose={onClose} title={a.label} width={460}>
        <div className="flex flex-col gap-3 text-[13px]">
          <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-warn">
            Sent for approval as APR-{sent}. Your role can only request this amount: it is booked once someone else approves it in Dealer desk → Approvals.
          </p>
          <div className="flex justify-end">
            <button type="button" className={btn} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Modal open onClose={onClose} title={a.label} width={460}>
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <p className="m-0 text-[12.5px] text-muted">
          {email}
          {acc ? ` · ${a.credit ? 'Credit' : 'Balance'} ${fmtUsd(a.credit ? acc.credit : acc.balance)}` : ''}
        </p>
        <p className={cx('m-0 rounded-lg px-3 py-2 text-xs', a.out ? 'bg-down-bg text-down' : 'bg-up-bg text-up')}>
          {a.out ? 'Taken' : 'Added'} as soon as you press {a.out ? 'Remove' : 'Add'}. {a.warn ?? ''}
        </p>
        {!a.credit && choices.length > 1 && (
          <label className="flex flex-col gap-1 text-[11.5px] text-muted">
            Account
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={input} aria-label="Account">
              {choices.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.kind === 'live' ? 'Live' : 'Demo'} · {fmtUsd(x.balance)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-[11.5px] text-muted">
          Amount
          <span className="flex items-baseline gap-1.5 rounded-xl border border-line-strong bg-bg px-3 py-1.5 focus-within:border-accent">
            <span className="text-[16px] font-semibold text-muted">$</span>
            <input
              autoFocus
              inputMode="decimal"
              value={amount}
              onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && setAmount(e.target.value)}
              placeholder="0.00"
              aria-label="Amount in USD"
              className="num min-w-0 flex-1 border-0 bg-transparent p-0 text-[24px] font-semibold text-fg outline-none"
            />
            <span className="text-xs font-semibold text-muted">USD</span>
          </span>
          {now && <span className="num">{after ? `${a.credit ? 'Credit' : 'Balance'} after: ${fmtUsd(after.toString())}` : `${a.credit ? 'Credit' : 'Balance'} now: ${fmtUsd(now.toString())}`}</span>}
        </label>
        <label className="flex flex-col gap-1 text-[11.5px] text-muted">
          Note for the client (optional)
          <textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={a.fallback} className={cx(input, 'h-auto py-2')} />
          <span>Shown to the client on this line in their transactions.</span>
        </label>
        {err && <p className="m-0 text-xs text-down">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !ok || !acc} className={cx('h-9 rounded-lg px-4 text-xs font-semibold text-white disabled:opacity-50', a.out ? 'bg-btn-short' : 'bg-accent')}>
            {busy ? <Spinner /> : `${a.out ? 'Remove' : 'Add'}${ok ? ` ${fmtUsd(amount)}` : ''}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** The client's Money card: Money in / Money out, each action with its tag and "?". */
export function MoneyCard({ accounts, email, onDone }: { accounts: AccountSummary[]; email: string; onDone: () => void }) {
  const [open, setOpen] = useState<Key | null>(null);
  const [help, setHelp] = useState<Key | null>(null);
  const hasLive = accounts.some((x) => x.kind === 'live');
  const list = ACTIONS.filter((a) => !a.credit || hasLive);
  const find = (k: Key | null) => list.find((a) => a.key === k);
  return (
    <section className="rounded-lg border border-line p-3" aria-label="Money">
      <h3 className="m-0 mb-1 text-sm font-semibold">Money</h3>
      {(['Money in', 'Money out'] as const).map((g) => (
        <div key={g}>
          <div className="px-1 pt-2 pb-1 text-[10.5px] font-semibold tracking-wider text-muted uppercase">{g}</div>
          {list
            .filter((a) => a.group === g)
            .map((a) => (
              <div key={a.key} className="flex items-center gap-1">
                <button type="button" onClick={() => setOpen(a.key)} className={cx('flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-raised', a.out ? 'text-down' : 'text-fg')}>
                  <span aria-hidden className="w-4 text-center text-muted">
                    {a.credit ? (a.out ? '−' : '+') : a.out ? '↑' : '↓'}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{a.label}</span>
                  <span className="rounded-full bg-up-bg px-2 py-0.5 text-[10.5px] font-semibold text-up">Instant</span>
                </button>
                <button type="button" aria-label={`What does ${a.label} do?`} title="What does this do?" onClick={() => setHelp(a.key)}
                  className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border border-line-strong text-[11px] font-semibold text-muted hover:text-fg">
                  ?
                </button>
              </div>
            ))}
        </div>
      ))}
      {find(help) && <Help a={find(help)!} onClose={() => setHelp(null)} onStart={() => { setOpen(help); setHelp(null); }} />}
      {find(open) && <ActionDialog a={find(open)!} accounts={accounts} email={email} onClose={() => setOpen(null)} onDone={onDone} />}
    </section>
  );
}
